import { apiFetch } from "@d12labs/client";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@d12labs/ui/alert-dialog";
import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { List, ListHeader, ListRow } from "@d12labs/ui/data-list";
import { EmptyState } from "@d12labs/ui/empty-state";
import { Input } from "@d12labs/ui/input";
import { Label } from "@d12labs/ui/label";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

type AdminUser = {
	id: string;
	name: string | null;
	email: string;
	emailVerified: boolean;
	image: string | null;
	createdAt: string;
	isAdmin: boolean;
};

function initials(name: string | null, email: string): string {
	const src = (name ?? "").trim();
	if (src) {
		const parts = src.split(/\s+/).filter(Boolean);
		return (
			((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || src.slice(0, 2).toUpperCase()
		);
	}
	return email.slice(0, 2).toUpperCase();
}

const COLUMNS = [
	{ label: "", width: "20px" },
	{ label: "Name", width: "1.4fr" },
	{ label: "Email", width: "1fr" },
	{ label: "Status", width: "100px" },
	{ label: "Joined", width: "110px", align: "right" as const },
];

function InviteUserButton({ onInvited }: { onInvited: () => void }) {
	const [open, setOpen] = useState(false);
	const [email, setEmail] = useState("");
	const [name, setName] = useState("");

	const invite = useMutation({
		mutationFn: () =>
			apiFetch<{ email: string }>("/api/users", {
				method: "POST",
				body: JSON.stringify({ email, name }),
			}),
		onSuccess: (row) => {
			toast.success(`Invite sent to ${row.email}`);
			setEmail("");
			setName("");
			setOpen(false);
			onInvited();
		},
		meta: { errorMessage: "Couldn't send invite" },
	});

	return (
		<AlertDialog open={open} onOpenChange={setOpen}>
			<AlertDialogTrigger asChild>
				<Button size="sm">
					<UserPlus className="size-4" />
					Invite
				</Button>
			</AlertDialogTrigger>
			<AlertDialogContent>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						invite.mutate();
					}}
				>
					<AlertDialogHeader>
						<AlertDialogTitle>Invite a user</AlertDialogTitle>
						<AlertDialogDescription>
							They'll get an email with a link to set their password.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<div className="flex flex-col gap-3 py-2">
						<div className="flex flex-col gap-2">
							<Label htmlFor="invite-name">Name</Label>
							<Input
								id="invite-name"
								value={name}
								onChange={(e) => setName(e.target.value)}
								required
								autoComplete="name"
							/>
						</div>
						<div className="flex flex-col gap-2">
							<Label htmlFor="invite-email">Email</Label>
							<Input
								id="invite-email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								autoComplete="email"
							/>
						</div>
					</div>
					<AlertDialogFooter>
						<AlertDialogCancel type="button">Cancel</AlertDialogCancel>
						<Button type="submit" disabled={invite.isPending}>
							{invite.isPending ? "Sending…" : "Send invite"}
						</Button>
					</AlertDialogFooter>
				</form>
			</AlertDialogContent>
		</AlertDialog>
	);
}

export function UsersPage() {
	const queryClient = useQueryClient();
	const users = useQuery({
		queryKey: ["users"],
		queryFn: () => apiFetch<AdminUser[]>("/api/users"),
		retry: false,
	});
	const access = useQuery({
		queryKey: ["admin", "access"],
		queryFn: () => apiFetch<{ isAdmin: boolean }>("/api/admin/access"),
		retry: false,
	});
	const isAdmin = access.data?.isAdmin === true;

	if (users.error) {
		return (
			<>
				<PageHeader eyebrow="System" icon={Users} title="Users" />
				<PageBody className="p-6">
					<p className="text-sm text-destructive">Couldn't load users. Try again in a moment.</p>
				</PageBody>
			</>
		);
	}

	const rows = users.data ?? [];

	return (
		<>
			<PageHeader
				eyebrow="System"
				icon={Users}
				title="Users"
				subtitle="Everyone in this deployment. New people join by invite."
				badges={rows.length ? <Badge tone="subtle">{rows.length}</Badge> : undefined}
				actions={
					isAdmin ? (
						<InviteUserButton
							onInvited={() => queryClient.invalidateQueries({ queryKey: ["users"] })}
						/>
					) : undefined
				}
			/>
			<PageBody className="p-6">
				<div className="mx-auto w-full max-w-5xl">
					{users.isLoading ? (
						<p className="text-sm text-muted-foreground">Loading…</p>
					) : rows.length === 0 ? (
						<EmptyState
							icon={Users}
							title="No users yet"
							body="Admins can invite people from this page."
						/>
					) : (
						<List>
							<ListHeader columns={COLUMNS} />
							{rows.map((u) => (
								<ListRow
									key={u.id}
									interactive={false}
									cells={[
										{
											value: (
												<span className="flex size-5 items-center justify-center overflow-hidden rounded-full border border-hairline bg-surface-2 text-[9px] font-medium text-ink">
													{u.image ? (
														<img src={u.image} alt="" className="size-full object-cover" />
													) : (
														initials(u.name, u.email)
													)}
												</span>
											),
											width: "20px",
										},
										{
											value: (
												<span className="flex items-center gap-2">
													<span className="truncate font-medium text-foreground">
														{u.name || u.email}
													</span>
													{u.isAdmin ? <Badge tone="primary">Admin</Badge> : null}
												</span>
											),
											width: "1.4fr",
										},
										{ value: u.email, width: "1fr", muted: true },
										{
											value: u.emailVerified ? (
												<Badge tone="success" dot="var(--color-success)">
													Verified
												</Badge>
											) : (
												<Badge tone="subtle">Unverified</Badge>
											),
											width: "100px",
										},
										{
											value: new Date(u.createdAt).toLocaleDateString(undefined, {
												month: "short",
												day: "numeric",
												year: "numeric",
											}),
											width: "110px",
											align: "right",
											muted: true,
										},
									]}
								/>
							))}
						</List>
					)}
				</div>
			</PageBody>
		</>
	);
}
